// =============================================
// File: src/hooks/useSpirometrySummary.js
// Purpose: Memoised top-level numbers + feedback strings for spirometry
// =============================================
import { useMemo } from "react";
import { formatDateLong, formatTime } from "../utils/time";

export function useSpirometrySummary({ date, data }) {
  const subtitle = useMemo(() => {
    const d = date instanceof Date ? date : new Date(date ?? Date.now());
    return `${formatDateLong(d)} · ${formatTime(d)}`;
  }, [date]);

  const ratioPct = useMemo(() => {
    const r = Number(data?.ratio);
    return Number.isFinite(r) ? Math.round(r * 100) : null;
  }, [data?.ratio]);

  const rr = useMemo(() => {
    const v = Number(data?.rr_median_ipi);
    return Number.isFinite(v) ? Math.round(v) : null;
  }, [data?.rr_median_ipi]);

  const lungAge = useMemo(() => {
    const v = Number(data?.lung_age_years);
    return Number.isFinite(v) ? v : null;
  }, [data?.lung_age_years]);

  const lungFitnessPct = useMemo(() => {
    const r = Number(data?.ratio);
    return Number.isFinite(r) ? Math.round(r * 100) : null;
  }, [data?.ratio]);

  const guidance = useMemo(() => {
    const notes = [];
    if (ratioPct != null) {
      if (ratioPct >= 85)
        notes.push(
          "Your Lung Fitness Score is strong. Keep up consistent aerobic work and occasional high-intensity intervals."
        );
      else if (ratioPct >= 70)
        notes.push(
          "Solid Lung Fitness. Focus on diaphragmatic breathing drills to push efficiency higher."
        );
      else
        notes.push(
          "Your Lung Fitness Score is below typical. Consider shorter, frequent breath-control sets and progressive aerobic conditioning."
        );
    }
    if (rr != null) {
      if (rr < 12)
        notes.push(
          "Very efficient resting respiratory rate — a good sign of conditioning."
        );
      else if (rr <= 20)
        notes.push("Respiratory rate is within a normal resting range.");
      else
        notes.push(
          "Elevated respiratory rate — try longer exhale drills and nasal-only warm-ups before tests."
        );
    }
    // if (lungAge != null)
    //   notes.push(
    //     `Estimated Lung Age: ${lungAge} years. Track this trend over time and aim for gradual improvements.`
    //   );
    return notes;
  }, [ratioPct, rr, lungAge]);

  return { subtitle, ratioPct, rr, lungAge, lungFitnessPct, guidance };
}
